/**
 * Phase 10 R5 — FinancialControlsSection
 *
 * Shop-level financial control settings (visibility mode + GP targets +
 * low-margin threshold). Lives inside the Labor Pricing tab.
 *
 * Two independent gates govern this round:
 *   - **Edit** financial settings: `ui_labor_pricing` (mounting) + role
 *     ∈ {OWNER, MANAGER}. SW/Tech see a read-only message (or nothing).
 *   - **See** financial data (GP card, snapshot): handled separately by
 *     `canSeeFinancials`. Toggling visibility mode does NOT change what the
 *     editor themselves sees.
 *
 * Targets are advisory ONLY. They drive warnings, never auto-pricing or
 * workflow blocks.
 */
import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Save, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  useShopFinancialSettings,
  useUpdateShopFinancialSettings,
} from '@/hooks/labor/useShopFinancialSettings';
import { useUserRole } from '@/hooks/useUserRole';
import { canEditFinancialSettings } from '@/lib/labor/resolveFinancialVisibility';
import type { FinancialVisibilityMode } from '@/lib/labor/resolveFinancialVisibility';

function parsePct(input: string): number | null | typeof NaN {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0 || n > 100) return NaN;
  return Math.round(n * 100) / 100;
}

export function FinancialControlsSection() {
  const { settings, isLoading } = useShopFinancialSettings();
  const { mutateAsync: save, isPending } = useUpdateShopFinancialSettings();
  const { role } = useUserRole();
  const canEdit = canEditFinancialSettings(role);

  const [mode, setMode] = useState<FinancialVisibilityMode>(
    'owner_manager_only',
  );
  const [tParts, setTParts] = useState('');
  const [tLabor, setTLabor] = useState('');
  const [tTotal, setTTotal] = useState('');
  const [lowFloor, setLowFloor] = useState('');

  useEffect(() => {
    if (isLoading) return;
    setMode(settings.financial_visibility_mode);
    setTParts(
      settings.target_parts_gp_percent != null
        ? String(settings.target_parts_gp_percent)
        : '',
    );
    setTLabor(
      settings.target_labor_gp_percent != null
        ? String(settings.target_labor_gp_percent)
        : '',
    );
    setTTotal(
      settings.target_total_gp_percent != null
        ? String(settings.target_total_gp_percent)
        : '',
    );
    setLowFloor(String(settings.low_margin_warn_threshold));
  }, [
    isLoading,
    settings.financial_visibility_mode,
    settings.target_parts_gp_percent,
    settings.target_labor_gp_percent,
    settings.target_total_gp_percent,
    settings.low_margin_warn_threshold,
  ]);

  const handleSave = async () => {
    if (!canEdit) return;
    const parts = parsePct(tParts);
    const labor = parsePct(tLabor);
    const total = parsePct(tTotal);
    const floor = parsePct(lowFloor);
    if (
      Number.isNaN(parts) ||
      Number.isNaN(labor) ||
      Number.isNaN(total) ||
      Number.isNaN(floor) ||
      floor === null
    ) {
      toast({
        title: 'Invalid value',
        description: 'Targets must be 0–100. Low-margin floor is required.',
        variant: 'destructive',
      });
      return;
    }
    try {
      await save({
        financial_visibility_mode: mode,
        target_parts_gp_percent: parts,
        target_labor_gp_percent: labor,
        target_total_gp_percent: total,
        low_margin_warn_threshold: floor,
      });
      toast({ title: 'Financial controls saved' });
    } catch (e: any) {
      toast({
        title: 'Save failed',
        description: e?.message ?? 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          Financial Controls
        </CardTitle>
        <CardDescription>
          Set who can see financial data and the gross-profit goals that drive
          warnings on the ticket and dashboard. Targets are{' '}
          <strong>advisory only</strong> — they never auto-adjust pricing or
          block tickets.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!canEdit && (
          <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/5 p-3 text-xs text-warning">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Read-only — only Owners and Managers can change financial
              controls.
            </span>
          </div>
        )}

        {/* Visibility mode */}
        <div className="space-y-2">
          <Label htmlFor="fin-vis-mode">Financial visibility</Label>
          <Select
            value={mode}
            onValueChange={(v) => setMode(v as FinancialVisibilityMode)}
            disabled={!canEdit}
          >
            <SelectTrigger id="fin-vis-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="owner_manager_only">
                Owners &amp; Managers only (recommended)
              </SelectItem>
              <SelectItem value="include_service_writer">
                Include Service Writers
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Technicians never see financial data. Editing this setting does
            not affect what you yourself can see.
          </p>
        </div>

        {/* Targets grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PctField
            id="t-parts"
            label="Target parts GP %"
            value={tParts}
            onChange={setTParts}
            disabled={!canEdit}
            placeholder="e.g. 60"
            help="Optional. Leave blank to disable parts target warnings."
          />
          <PctField
            id="t-labor"
            label="Target labor GP %"
            value={tLabor}
            onChange={setTLabor}
            disabled={!canEdit}
            placeholder="e.g. 65"
            help="Optional. Leave blank to disable labor target warnings."
          />
          <PctField
            id="t-total"
            label="Target combined GP %"
            value={tTotal}
            onChange={setTTotal}
            disabled={!canEdit}
            placeholder="e.g. 55"
            help="Optional. Leave blank to disable combined target warnings."
          />
        </div>

        {/* Low margin floor */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PctField
            id="low-floor"
            label="Low-margin floor %"
            value={lowFloor}
            onChange={setLowFloor}
            disabled={!canEdit}
            placeholder="10"
            help="Severity floor — tickets with combined GP% below this are flagged regardless of targets. Default 10%."
          />
        </div>

        <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground space-y-1">
          <p className="font-medium text-foreground">How warnings combine</p>
          <p>
            Targets and the low-margin floor are independent. A ticket can fire
            both a "below target" warning <em>and</em> a "low margin" warning
            at the same time — they answer different questions ("are we hitting
            our goal?" vs "is this dangerously thin?"). Both are non-blocking.
          </p>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={!canEdit || isPending}>
            {isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save financial controls
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PctField({
  id,
  label,
  value,
  onChange,
  disabled,
  placeholder,
  help,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (s: string) => void;
  disabled: boolean;
  placeholder: string;
  help: string;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type="number"
          inputMode="decimal"
          min="0"
          max="100"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          className="pr-7"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
          %
        </span>
      </div>
      <p className="text-xs text-muted-foreground">{help}</p>
    </div>
  );
}
